import { Document, Page, StyleSheet, Text, View, Image } from "@react-pdf/renderer";
import MeratractorLogo from "../../../../img/logo2.png";
import FacebookIcon from "../../../../img/facebook.png";
import LinkedinIcon from "../../../../img/linkedin.png";
import TwitterIcon from "../../../../img/twitter.png";

const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    fontFamily: "Times-Roman",
    paddingTop: 90,
    paddingBottom: 120,
    paddingHorizontal: 50,
    lineHeight: 1.4,
  },
  // Header styles
  header: {
    position: "absolute",
    top: 20,
    left: 50,
    right: 50,
  },
  logoContainer: {
    alignItems: "flex-end",
    marginBottom: 8,
  },
  logo: {
    width: 180,
    height: 36,
  },
  redLine: {
    height: 2,
    backgroundColor: "#cc0000",
    marginTop: 5,
  },
  // Passport photo styles.
  // The Image is rendered as a top-level absolute element (not nested inside a
  // clipping container) so that external e-sign tools — which flatten and
  // re-save the PDF — don't strip it. Nested overflow:hidden + objectFit:cover
  // produces a clipped Form XObject that several signing tools mishandle,
  // dropping the image from the resaved content stream.
  // The upload canvas already crops the source to the exact 85:105 aspect
  // ratio, so we can place the image with absolute dimensions and skip
  // objectFit entirely.
  passportPhoto: {
    position: "absolute",
    top: 90,
    right: 50,
    width: 85,
    height: 105,
    borderWidth: 1,
    borderColor: "#000",
  },
  passportPhotoContainer: {
    position: "absolute",
    top: 90,
    right: 50,
    width: 85,
    height: 105,
    borderWidth: 1,
    borderColor: "#000",
    backgroundColor: "#f9f9f9",
    justifyContent: "center",
    alignItems: "center",
  },
  passportPhotoPlaceholder: {
    fontSize: 7,
    color: "#999",
    textAlign: "center",
  },
  // Title
  title: {
    fontSize: 11,
    fontWeight: "bold",
    fontFamily: "Times-Bold",
    textAlign: "center",
    textDecoration: "underline",
    marginBottom: 15,
    marginTop: 10,
    paddingRight: 100, // Space for passport photo
  },
  // Content area with passport photo - limits width to avoid overlap
  contentWithPhoto: {
    marginRight: 95, // Leave space for passport photo (85px width + padding)
  },
  // Form row
  formRow: {
    flexDirection: "row",
    marginBottom: 8,
    alignItems: "flex-start",
  },
  formLabel: {
    width: "40%",
    fontSize: 10,
  },
  formValue: {
    width: "60%",
    fontSize: 10,
  },
  bold: {
    fontWeight: "bold",
    fontFamily: "Times-Bold",
  },
  underline: {
    textDecoration: "underline",
  },
  // Table styles
  table: {
    marginVertical: 10,
    borderWidth: 1,
    borderColor: "#000",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
  },
  tableRowLast: {
    flexDirection: "row",
  },
  tableCell: {
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 4,
    fontSize: 8,
    overflow: "hidden",
    flexWrap: "wrap",
  },
  tableCellLast: {
    padding: 4,
    fontSize: 8,
    overflow: "hidden",
    flexWrap: "wrap",
  },
  // Style for partner name text to wrap properly
  partnerNameText: {
    fontSize: 7,
    wordBreak: "break-word",
  },
  // Partners table columns
  partnerCol1: {
    width: "20%",
  },
  partnerCol2: {
    width: "20%",
  },
  partnerCol3: {
    width: "20%",
  },
  partnerCol4: {
    width: "20%",
  },
  partnerCol5: {
    width: "20%",
  },
  // Facility table columns
  facilityCol1: {
    width: "20%",
  },
  facilityCol2: {
    width: "10%",
  },
  facilityCol3: {
    width: "10%",
  },
  facilityCol4: {
    width: "15%",
  },
  facilityCol5: {
    width: "15%",
  },
  facilityCol6: {
    width: "15%",
  },
  facilityCol7: {
    width: "15%",
  },
  // Sign box
  signBox: {
    position: "absolute",
    right: 0,
    top: 150,
    width: 60,
    height: 200,
    borderWidth: 1,
    borderColor: "#ccc",
    justifyContent: "center",
    alignItems: "center",
  },
  signText: {
    fontSize: 10,
    color: "#999",
    transform: "rotate(-90deg)",
  },
  // Section heading
  sectionHeading: {
    fontSize: 10,
    fontWeight: "bold",
    fontFamily: "Times-Bold",
    textDecoration: "underline",
    marginTop: 15,
    marginBottom: 10,
  },
  // Paragraph
  paragraph: {
    fontSize: 10,
    lineHeight: 1.5,
    textAlign: "justify",
    marginBottom: 10,
  },
  // List item
  listItem: {
    fontSize: 10,
    marginLeft: 20,
    marginBottom: 4,
  },
  // Facility required section
  facilityRow: {
    flexDirection: "row",
    marginBottom: 6,
  },
  facilityLabel: {
    width: "25%",
    fontSize: 10,
  },
  facilityValue: {
    width: "75%",
    fontSize: 10,
  },
  // Footer styles
  footer: {
    position: "absolute",
    bottom: 20,
    left: 50,
    right: 50,
    borderTopWidth: 1,
    borderTopColor: "#000",
    paddingTop: 8,
  },
  footerCompanyName: {
    fontSize: 11,
    fontWeight: "bold",
    fontFamily: "Times-Bold",
    color: "#000000",
    marginBottom: 3,
  },
  footerText: {
    fontSize: 7,
    lineHeight: 1.3,
    marginBottom: 2,
  },
  footerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 5,
  },
  footerSocialContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 12,
  },
  footerSocialIcon: {
    width: 14,
    height: 14,
    marginRight: 3,
  },
  footerSocialText: {
    fontSize: 7,
  },
  followUsText: {
    fontSize: 7,
    marginRight: 8,
  },
});

const MTApplicationFormPDF = ({ formData, limitSettingData }) => {
  // Dynamic fields from formData - using existing keys
  const dealerName = formData?.personName || "";
  const companyName = formData?.companyName || "";

  // Passport photo - can come from formData or limitSettingData.
  // The upload handler normalises it to a JPEG data URL at passport dimensions,
  // so @react-pdf/renderer's resolveBase64Image path handles it without fetch.
  const passportPhoto = limitSettingData?.passportPhoto || formData?.passportPhoto || null;
  const businessType = formData?.businessType?.toLowerCase() || "";
  const constitution = formData?.businessType?.toUpperCase() || "Individual / Partnership";
  const managementDetails = formData?.personName || "";
  const registeredAddress = formData?.address || "";
  const contactPerson = formData?.personName || "";
  const contactNo = formData?.personPhone || "";
  const staff = "";
  const emailId = formData?.email || "";
  const premiseOwned = "";
  const ownedValue = "";
  const rentedValue = "";
  const yearsInBusiness = "";
  const last6MonthsSales = "";
  const creditFacility = "";
  const unitsRequired = "";
  const bankNames = "";
  const facilityAmount = "10,00,000";
  const place = "";

  // Get partners data - array of partner names or partner objects
  const isIndividual = businessType === "individual";
  const isProprietor = businessType === "proprietor" || businessType === "proprietorship";
  const isPartnership = businessType === "partnership";
  const isPrivateLimited = businessType === "pvt_ltd" || businessType === "private_limited";

  // Use partners array if provided, otherwise use personName for proprietorship
  const partners = limitSettingData?.partners?.length > 0
    ? limitSettingData.partners
    : (isProprietor && dealerName ? [dealerName] : []);

  // Calculate number of columns needed (minimum 4, add more if partners > 4)
  const minPartnerColumns = 4;
  const partnerColumnCount = Math.max(minPartnerColumns, partners.length);

  // Calculate column width dynamically - reduce label column when many partners
  const labelColumnWidth = partnerColumnCount > 4 ? 14 : 18; // percentage for first column
  const partnerColumnWidth = (100 - labelColumnWidth) / partnerColumnCount;

  // Generate partner column headers - shorter headers when many columns
  const getPartnerColumnHeader = (index) => {
    if (partnerColumnCount > 4) {
      // Short headers for many columns
      if (index === 0) return `1st Partner\nName`;
      return `${index + 1}${index === 1 ? 'nd' : index === 2 ? 'rd' : 'th'} Partner\nName`;
    }
    // Original headers for 4 or fewer columns
    if (index === 0) return `Proprietor Name/1st\nRelationship`;
    if (index === 1) return `2nd Partner Name/2nd\nRelationship`;
    if (index === 2) return `3rd Partner\nName/3rd &\nRelationship`;
    if (index === 3) return `4th Partner Name\n& Relationship`;
    return `${index + 1}th Partner Name\n& Relationship`;
  };

  // Get partner name for a specific index (handles both string and object partners)
  const getPartnerName = (index) => {
    const partner = partners[index];
    if (!partner) return "";
    if (typeof partner === 'object') return partner.name || "";
    return partner;
  };

  const Header = () => (
    <View style={styles.header} fixed>
      <View style={styles.logoContainer}>
        <Image style={styles.logo} src={MeratractorLogo} />
      </View>
      <View style={styles.redLine} />
    </View>
  );

  const Footer = () => (
    <View style={styles.footer} fixed>
      <Text style={styles.footerCompanyName}>Krishak Sevita Online Solutions Private Limited</Text>
      <Text style={styles.footerText}>
        <Text style={styles.bold}>Registered Office:</Text> Flat No 303, Building B, Kumar Gulmohar, Near Nancy Gardens, Wanawadi, Pune- 411040, Maharashtra, IN
      </Text>
      <Text style={styles.footerText}>
        CIN: U74900PN2015OPC156481  |  PAN: AAFCK9361C  |  GST No Maharashtra: 27AAFCK9361C1ZH
      </Text>
      <Text style={styles.footerText}>
        Visit us at www.meratractor.com write to us at contact@meratractor.com or call on <Text style={styles.bold}>8888009900</Text>
      </Text>
      <View style={styles.footerRow}>
        <Text style={styles.followUsText}>Follow us on</Text>
        <View style={styles.footerSocialContainer}>
          <Image style={styles.footerSocialIcon} src={FacebookIcon} />
          <Text style={styles.footerSocialText}>facebook.com/meratractor</Text>
        </View>
        <View style={styles.footerSocialContainer}>
          <Image style={styles.footerSocialIcon} src={LinkedinIcon} />
          <Text style={styles.footerSocialText}>linkedin.com/in/meratractor</Text>
        </View>
        <View style={styles.footerSocialContainer}>
          <Image style={styles.footerSocialIcon} src={TwitterIcon} />
          <Text style={styles.footerSocialText}>twitter.com/meratractor</Text>
        </View>
      </View>
    </View>
  );

  const SignBox = () => (
    <View style={styles.signBox}>
      <Text style={styles.signText}>Sign Here</Text>
    </View>
  );

  return (
    <Document>
      {/* Page 1 */}
      <Page size="A4" style={styles.page} wrap>
        <Header />

        {/* Passport Photo - Top Right Corner (First Page Only).
            When a photo exists, render the Image as a top-level absolute
            element so external e-sign tools do not drop it during flattening.
            When no photo, fall back to the placeholder box. */}
        {passportPhoto ? (
          <Image style={styles.passportPhoto} src={passportPhoto} />
        ) : (
          <View style={styles.passportPhotoContainer}>
            <Text style={styles.passportPhotoPlaceholder}>Passport{"\n"}Photo</Text>
          </View>
        )}

        <Text style={styles.title}>Preliminary Information cum Loan Application Form</Text>

        {/* Form Fields 1-3 - wrapped to avoid passport photo overlap */}
        <View style={styles.contentWithPhoto}>
          <View style={styles.formRow}>
            <Text style={styles.formLabel}>1. Name of the Dealer</Text>
            <Text style={styles.formValue}>: {isIndividual ? dealerName : `${companyName} (Prop:${dealerName})`}</Text>
          </View>

          <View style={styles.formRow}>
            <Text style={styles.formLabel}>2. Constitution</Text>
            <Text style={styles.formValue}>: {constitution}</Text>
          </View>

          <View style={styles.formRow}>
            <Text style={styles.formLabel}>3. Management Details</Text>
            <Text style={styles.formValue}>: {managementDetails}</Text>
          </View>
        </View>

        {/* Partners Table - Only show if not individual */}
        {/* {!isIndividual && ( */}
          <View style={styles.table}>
            {/* Header Row */}
            <View style={styles.tableRow}>
              <View style={[styles.tableCell, { width: `${labelColumnWidth}%` }]}>
                <Text></Text>
              </View>
              {Array.from({ length: partnerColumnCount }).map((_, index) => (
                <View
                  key={`header-${index}`}
                  style={[
                    index === partnerColumnCount - 1 ? styles.tableCellLast : styles.tableCell,
                    { width: `${partnerColumnWidth}%` }
                  ]}
                >
                  <Text style={styles.partnerNameText}>{getPartnerColumnHeader(index)}</Text>
                </View>
              ))}
            </View>
            {/* Proprietorship Row */}
            <View style={styles.tableRow}>
              <View style={[styles.tableCell, { width: `${labelColumnWidth}%` }]}>
                <Text>Proprietorship</Text>
              </View>
              {Array.from({ length: partnerColumnCount }).map((_, index) => (
                <View
                  key={`prop-${index}`}
                  style={[
                    index === partnerColumnCount - 1 ? styles.tableCellLast : styles.tableCell,
                    { width: `${partnerColumnWidth}%` }
                  ]}
                >
                  <Text style={styles.partnerNameText}>{isProprietor || isIndividual ? getPartnerName(index) : ""}</Text>
                </View>
              ))}
            </View>
            {/* Partnership Row */}
            <View style={styles.tableRowLast}>
              <View style={[styles.tableCell, { width: `${labelColumnWidth}%` }]}>
                <Text>Partnership</Text>
              </View>
              {Array.from({ length: partnerColumnCount }).map((_, index) => (
                <View
                  key={`part-${index}`}
                  style={[
                    index === partnerColumnCount - 1 ? styles.tableCellLast : styles.tableCell,
                    { width: `${partnerColumnWidth}%` }
                  ]}
                >
                  <Text style={styles.partnerNameText}>{isPartnership ? getPartnerName(index) : ""}</Text>
                </View>
              ))}
            </View>
          </View>
        {/* )} */}

        {/* Form Fields 4-15 */}
        <View style={styles.formRow}>
          <Text style={styles.formLabel}>4. Registered address</Text>
          <Text style={styles.formValue}>: {registeredAddress}</Text>
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>5. Contact Person</Text>
          <Text style={styles.formValue}>: {contactPerson}</Text>
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>6. Contact No</Text>
          <Text style={styles.formValue}>: {contactNo}</Text>
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>7. Staff</Text>
          <Text style={styles.formValue}>: {staff}</Text>
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>8. Email Id</Text>
          <Text style={styles.formValue}>: {emailId}</Text>
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>9. If the office premise owned/rented</Text>
          <Text style={styles.formValue}>: {premiseOwned}</Text>
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>10. If owned, current value (INR)</Text>
          <Text style={styles.formValue}>: {ownedValue}</Text>
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>11. If rented, monthly rental (INR)</Text>
          <Text style={styles.formValue}>: {rentedValue}</Text>
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>12. No of years in current business</Text>
          <Text style={styles.formValue}>: {yearsInBusiness}</Text>
        </View>

        <View style={styles.formRow}>
          <Text style={[styles.formLabel, { width: "100%" }]}>
            13. Dealing with company Last 6 months sales and purchase of tractors. In nos month wise : {last6MonthsSales}
          </Text>
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>14. Does Dealer enjoy any credit facility</Text>
          <Text style={styles.formValue}>: {creditFacility}</Text>
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>15. No of units loan required</Text>
          <Text style={styles.formValue}>: {unitsRequired}</Text>
        </View>

        <Footer />
      </Page>

      {/* Page 2 */}
      <Page size="A4" style={styles.page} wrap>
        <Header />

        {/* Types of Facility Table */}
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={[styles.tableCell, styles.facilityCol1]}><Text>Types of Facility</Text></View>
            <View style={[styles.tableCell, styles.facilityCol2]}><Text>CC</Text></View>
            <View style={[styles.tableCell, styles.facilityCol3]}><Text>OD</Text></View>
            <View style={[styles.tableCell, styles.facilityCol4]}><Text>Term{"\n"}Loan</Text></View>
            <View style={[styles.tableCell, styles.facilityCol5]}><Text>Term{"\n"}Loan</Text></View>
            <View style={[styles.tableCell, styles.facilityCol6]}><Text>Commercial vehicle{"\n"}loan</Text></View>
            <View style={[styles.tableCellLast, styles.facilityCol7]}><Text>Commercial vehicle{"\n"}loan</Text></View>
          </View>
          <View style={styles.tableRow}>
            <View style={[styles.tableCell, styles.facilityCol1]}><Text>Name of the Bank/Financer</Text></View>
            <View style={[styles.tableCell, styles.facilityCol2]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol3]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol4]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol5]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol6]}><Text></Text></View>
            <View style={[styles.tableCellLast, styles.facilityCol7]}><Text></Text></View>
          </View>
          <View style={styles.tableRow}>
            <View style={[styles.tableCell, styles.facilityCol1]}><Text>Amount (In Rs Lacs)</Text></View>
            <View style={[styles.tableCell, styles.facilityCol2]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol3]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol4]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol5]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol6]}><Text></Text></View>
            <View style={[styles.tableCellLast, styles.facilityCol7]}><Text></Text></View>
          </View>
          <View style={styles.tableRow}>
            <View style={[styles.tableCell, styles.facilityCol1]}><Text>Tenor (months)</Text></View>
            <View style={[styles.tableCell, styles.facilityCol2]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol3]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol4]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol5]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol6]}><Text></Text></View>
            <View style={[styles.tableCellLast, styles.facilityCol7]}><Text></Text></View>
          </View>
          <View style={styles.tableRow}>
            <View style={[styles.tableCell, styles.facilityCol1]}><Text>ROI (% per annum)</Text></View>
            <View style={[styles.tableCell, styles.facilityCol2]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol3]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol4]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol5]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol6]}><Text></Text></View>
            <View style={[styles.tableCellLast, styles.facilityCol7]}><Text></Text></View>
          </View>
          <View style={styles.tableRow}>
            <View style={[styles.tableCell, styles.facilityCol1]}><Text>Collateral</Text></View>
            <View style={[styles.tableCell, styles.facilityCol2]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol3]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol4]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol5]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol6]}><Text></Text></View>
            <View style={[styles.tableCellLast, styles.facilityCol7]}><Text></Text></View>
          </View>
          <View style={styles.tableRowLast}>
            <View style={[styles.tableCell, styles.facilityCol1]}><Text>Value of Collateral (In Rs{"\n"}Lacs)</Text></View>
            <View style={[styles.tableCell, styles.facilityCol2]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol3]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol4]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol5]}><Text></Text></View>
            <View style={[styles.tableCell, styles.facilityCol6]}><Text></Text></View>
            <View style={[styles.tableCellLast, styles.facilityCol7]}><Text></Text></View>
          </View>
        </View>

        <View style={[styles.formRow, { marginTop: 15 }]}>
          <Text style={{ fontSize: 10 }}>16. Name of the Banks where Dealer has current accounts : {bankNames}</Text>
        </View>

        <Text style={styles.sectionHeading}>Distribution Information:</Text>

        <Text style={[styles.sectionHeading, { marginTop: 10 }]}>List of Documents to be furnished/being furnished:</Text>

        <Text style={styles.listItem}>1       PAN Card and Aadhar Card copy of the proprietor/partners</Text>
        <Text style={styles.listItem}>2       PAN Card and Address Proof of the firm</Text>
        <Text style={styles.listItem}>3       Copy of the GST Certificate</Text>
        <Text style={styles.listItem}>4       Audited financials of last 3 years</Text>
        <Text style={styles.listItem}>5       Account Statement of the operative bank accounts for last 1 year</Text>
        <Text style={styles.listItem}>6       Copy of the sanction letter if firm avails any bank finance</Text>
        <Text style={styles.listItem}>7       Latest ITR</Text>

        <Footer />
      </Page>

      {/* Page 3 */}
      <Page size="A4" style={styles.page} wrap>
        <Header />

        <Text style={[styles.sectionHeading, { textDecoration: "none" }]}>Facility required</Text>

        <View style={styles.facilityRow}>
          <Text style={styles.facilityLabel}>Type</Text>
          <Text style={styles.facilityValue}>: Inventory Finance Facility</Text>
        </View>

        <View style={styles.facilityRow}>
          <Text style={styles.facilityLabel}>Amount (Rs.)</Text>
          <Text style={styles.facilityValue}>: {facilityAmount}</Text>
        </View>

        <View style={styles.facilityRow}>
          <Text style={styles.facilityLabel}>Anchor Company</Text>
          <Text style={styles.facilityValue}>: Krishak Sevita Online Solutions Private Limited</Text>
        </View>

        <View style={styles.facilityRow}>
          <Text style={styles.facilityLabel}>Rate (%)</Text>
          <Text style={styles.facilityValue}>: 18% IRR +1% PF</Text>
        </View>

        <View style={styles.facilityRow}>
          <Text style={styles.facilityLabel}>Tenure (Days)</Text>
          <Text style={styles.facilityValue}>: 120 Days (Includes grace period where additional interest is levied)</Text>
        </View>

        <Text style={[styles.sectionHeading, { marginTop: 20 }]}>We hereby undertake and confirm that:</Text>

        <Text style={styles.paragraph}>
          All the particulars and information furnished in the application are true, correct and complete and up-to-date in all respects. We undertake to inform KSOS regarding any change in our abovementioned details and to provide any other information that KSOS may require.
        </Text>

        <Text style={styles.paragraph}>
          We, the user and Guarantor(s) confirm that KSOS can disclose the information and data furnished by them to KSOS and also those regarding the credit facility/ies enjoyed by the user, conduct of accounts and guarantee obligations undertaken by Guarantor(s) to the Credit Information Bureau (India) Ltd. ("CIBIL"), or RBI or any other agencies specified by RBI who are authorized (India) to seek and publish information, and also to obtain information from these agencies/authorities/etc. with respect to the user and/or Guarantors, so as to enable KSOS to appraise the User's application for grant of such facility/ies.
        </Text>

        <Text style={styles.paragraph}>
          We hereby irrevocably agree to and consent that KSOS may disclose at any time and share with or in any manner make available to any agencies, bureaus, affiliate companies, firms, associations, corporate bodies and other persons any information about me/us where such disclosure is necessary under law or where there is a duty to the public to disclose such information or where the interest or KSOS requires such disclosure. Additionally, KSOS may disclose such personal and financial information about me/us that is in the public domain. We hereby agree and consent that KSOS may share/distribute any information including my/our personal information provided by me/us to its affiliates and subsidiaries including third parties where KSOS deems it necessary. Additionally, we consent to KSOS using personal information and financial information for giving credit report and credit opinions about me/us to other Banks and financial institutions. We understand and agree that the sanction / disbursement will be on the sole discretion of KSOS only and we shall not make any claim against KSOS in case this request is rejected or in case no loan is sanctioned or in case no disbursement is made for any reason whatsoever. We further understand and agree that the charges paid to KSOS towards out of pocket expenses and / or log-in/ processing fees are non-refundable and upon the request for loan facilities being withdrawn by us or being rejected by KSOS for any reason whatsoever in the pre-sanction or post-sanction stage, we will not be entitled to any refund in part or in full.
        </Text>

        <Footer />
      </Page>

      {/* Page 4 */}
      <Page size="A4" style={styles.page}>
        <Header />

        <Text style={styles.paragraph}>
          We agree that the facility shall be governed by terms and conditions stipulated by KSOS which maybe in force from time to time. We will adhere to all terms and conditions governing the sanction of facility and will complete the required documentation at the earliest on sanction of the loan. We also understand that this request is considered valid until we cancel the same.
        </Text>

        <View style={{ marginTop: 30 }}>
          <Text style={styles.paragraph}>Thanking you,</Text>
          <Text style={styles.paragraph}>Yours faithfully,</Text>
        </View>

        <View style={{ marginTop: 20 }}>
          <Text style={{ fontSize: 10 }}>Place: {place}</Text>
        </View>

        <View style={{ marginTop: 40 }}>
          <Text style={{ fontSize: 10 }}>Director's/Authorised Signatory</Text>
        </View>

        <Footer />
      </Page>
    </Document>
  );
};

export default MTApplicationFormPDF;
